import { useState, useEffect, useCallback, useMemo } from 'react';
import Editor from '@monaco-editor/react';
import { compile, formatErrors, CompilerOutput } from './compiler';
import { examples, defaultExample, Example } from './examples';
import LZString from 'lz-string';

// Monaco editor theme
const pyramidTheme = {
  base: 'vs-dark' as const,
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '569cd6', fontStyle: 'bold' },
    { token: 'type', foreground: '4ec9b0' },
    { token: 'string', foreground: 'ce9178' },
    { token: 'number', foreground: 'b5cea8' },
    { token: 'comment', foreground: '6a9955', fontStyle: 'italic' },
    { token: 'identifier', foreground: '9cdcfe' },
  ],
  colors: {
    'editor.background': '#1e1e2e',
    'editor.foreground': '#cdd6f4',
    'editorLineNumber.foreground': '#6c7086',
    'editor.lineHighlightBackground': '#313244',
    'editor.selectionBackground': '#45475a',
  },
};

// Monaco language configuration for Pyramid DSL
const pyramidLanguage = {
  defaultToken: '',
  tokenPostfix: '.pyramid',
  keywords: ['screen', 'data', 'type', 'if', 'else', 'ForEach', 'true', 'false', 'null'],
  
  tokenizer: {
    root: [
      [/\/\/.*$/, 'comment'],
      [/\/\*/, 'comment', '@comment'],
      [/"([^"\\]|\\.)*"/, 'string'],
      [/#[0-9A-Fa-f]{6,8}/, 'number'],
      [/\b(screen|data|type|if|else|ForEach)\b/, 'keyword'],
      [/\b(true|false|null)\b/, 'keyword'],
      [/\b(String|Int|Float|Bool|Color|Date|DateTime)\b/, 'type'],
      [/\b[A-Z][a-zA-Z0-9]*\b/, 'type'],
      [/\b[a-z][a-zA-Z0-9]*\b/, 'identifier'],
      [/[0-9]+(\.[0-9]+)?/, 'number'],
      [/[{}()\[\]<>]/, '@brackets'],
      [/[,.:;]/, 'delimiter'],
      [/[+\-*\/%=!<>&|?]/, 'operator'],
    ],
    comment: [
      [/[^/*]+/, 'comment'],
      [/\*\//, 'comment', '@pop'],
      [/[/*]/, 'comment'],
    ],
  },
};

type Tab = 'json' | 'preview';

function App() {
  const [code, setCode] = useState(defaultExample.code);
  const [output, setOutput] = useState<CompilerOutput | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('json');
  const [_shareUrl, setShareUrl] = useState<string | null>(null);
  const [showExamples, setShowExamples] = useState(false);
  const [showCopied, setShowCopied] = useState(false);

  // Load code from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const compressed = params.get('code');
    if (compressed) {
      try {
        const decompressed = LZString.decompressFromEncodedURIComponent(compressed);
        if (decompressed) setCode(decompressed);
      } catch (e) {
        console.error('Failed to decompress code from URL:', e);
      }
    }
  }, []);

  // Compile on code change
  useEffect(() => {
    const timer = setTimeout(() => {
      const result = compile(code);
      setOutput(result);
    }, 300);

    return () => clearTimeout(timer);
  }, [code]);

  const handleEditorMount = useCallback((monaco: typeof import('monaco-editor')) => {
    monaco.languages.register({ id: 'pyramid' });
    monaco.languages.setMonarchTokensProvider('pyramid', pyramidLanguage as import('monaco-editor').languages.IMonarchLanguage);
    monaco.editor.defineTheme('pyramid-dark', pyramidTheme);
  }, []);

  const handleShare = useCallback(() => {
    const compressed = LZString.compressToEncodedURIComponent(code);
    const url = `${window.location.origin}${window.location.pathname}?code=${compressed}`;
    setShareUrl(url);
    navigator.clipboard.writeText(url);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  }, [code]);

  const loadExample = useCallback((example: Example) => {
    setCode(example.code);
    setShowExamples(false);
    // Clear URL params
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const jsonOutput = useMemo(() => {
    if (!output) return '';
    if (!output.success) return formatErrors(output.errors, code);
    return JSON.stringify(output.screens, null, 2);
  }, [output, code]);

  return (
    <div className="h-screen flex flex-col bg-gray-900 text-white">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <svg className="w-8 h-8" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="pyramidGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#3b82f6' }} />
                <stop offset="100%" style={{ stopColor: '#1d4ed8' }} />
              </linearGradient>
            </defs>
            <polygon points="50,10 90,85 10,85" fill="url(#pyramidGrad)" />
            <polygon points="50,10 50,85 10,85" fill="#1e40af" opacity="0.5" />
          </svg>
          <div>
            <h1 className="text-lg font-bold">Pyramid DSL Playground</h1>
            <p className="text-xs text-gray-400">Try server-driven UI syntax live</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Examples dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExamples(!showExamples)}
              className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
              Examples
            </button>

            {showExamples && (
              <div className="absolute right-0 mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                {examples.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => loadExample(example)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg transition-colors"
                  >
                    <div className="font-medium text-sm">{example.name}</div>
                    <div className="text-xs text-gray-400">{example.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Share button */}
          <button
            onClick={handleShare}
            className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-500 rounded-lg flex items-center gap-2 transition-colors"
          >
            {showCopied ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share
              </>
            )}
          </button>

          {/* Link to Pyramid */}
          <a
            href="https://pyramid.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 text-sm border border-gray-600 hover:border-gray-500 rounded-lg transition-colors"
          >
            Learn More →
          </a>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor panel */}
        <div className="w-1/2 flex flex-col border-r border-gray-700">
          <div className="px-4 py-2 bg-gray-800 border-b border-gray-700 text-sm font-medium text-gray-300">
            pyramid.dsl
          </div>
          <div className="flex-1">
            <Editor
              height="100%"
              defaultLanguage="pyramid"
              theme="pyramid-dark"
              value={code}
              onChange={(value) => setCode(value || '')}
              beforeMount={handleEditorMount}
              options={{
                fontSize: 14,
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                minimap: { enabled: false },
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2,
                wordWrap: 'on',
                padding: { top: 16 },
              }}
            />
          </div>
        </div>

        {/* Output panel */}
        <div className="w-1/2 flex flex-col">
          {/* Tabs */}
          <div className="flex bg-gray-800 border-b border-gray-700">
            <button
              onClick={() => setActiveTab('json')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'json'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              JSON Output
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'preview'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              Preview
            </button>

            {/* Status indicator */}
            <div className="flex-1 flex items-center justify-end px-4">
              {output && (
                <div className={`flex items-center gap-2 text-xs ${output.success ? 'text-green-400' : 'text-red-400'}`}>
                  <div className={`w-2 h-2 rounded-full ${output.success ? 'bg-green-400' : 'bg-red-400'}`} />
                  {output.success ? `${output.screens.length} screen${output.screens.length !== 1 ? 's' : ''} compiled` : `${output.errors.length} error${output.errors.length !== 1 ? 's' : ''}`}
                </div>
              )}
            </div>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-auto">
            {activeTab === 'json' && (
              <div className="h-full">
                <Editor
                  height="100%"
                  defaultLanguage="json"
                  theme="vs-dark"
                  value={jsonOutput}
                  options={{
                    readOnly: true,
                    fontSize: 13,
                    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                    minimap: { enabled: false },
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    wordWrap: 'on',
                    padding: { top: 16 },
                  }}
                />
              </div>
            )}

            {activeTab === 'preview' && (
              <div className="p-4 flex items-center justify-center h-full">
                {output?.success && output.screens.length > 0 ? (
                  <MobilePreview screens={output.screens} />
                ) : (
                  <div className="text-gray-500 text-center">
                    <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm">Write some DSL code to see a preview</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Click outside to close examples */}
      {showExamples && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowExamples(false)}
        />
      )}
    </div>
  );
}

// Mobile device preview component
function MobilePreview({ screens }: { screens: import('./compiler').CompiledScreen[] }) {
  const screen = screens[0];

  return (
    <div className="relative">
      {/* Phone frame */}
      <div className="w-[320px] h-[640px] bg-gray-800 rounded-[40px] p-3 shadow-2xl border border-gray-700">
        {/* Screen */}
        <div className="w-full h-full bg-white rounded-[32px] overflow-hidden flex flex-col">
          {/* Status bar */}
          <div className="h-8 bg-gray-100 flex items-center justify-between px-6 text-xs text-gray-600">
            <span>9:41</span>
            <div className="flex gap-1">
              <span>📶</span>
              <span>🔋</span>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-4 bg-white">
            <LayoutRenderer node={screen.layout} />
          </div>

          {/* Home indicator */}
          <div className="h-8 flex items-center justify-center bg-white">
            <div className="w-32 h-1 bg-gray-300 rounded-full" />
          </div>
        </div>
      </div>

      {/* Screen name label */}
      <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-gray-500">
        {screen.name}
        {screen.route && <span className="ml-2 text-gray-600">{screen.route}</span>}
      </div>
    </div>
  );
}

// Render a layout node as wireframe
function LayoutRenderer({ node }: { node: import('./compiler').JsonNode }) {
  const { type, props, children } = node;

  // Render based on component type
  switch (type) {
    case 'Column':
      return (
        <div
          className="flex flex-col"
          style={{
            padding: typeof props?.padding === 'number' ? props.padding : 0,
            gap: typeof props?.spacing === 'number' ? props.spacing : 0,
          }}
        >
          {children?.map((child, i) => <LayoutRenderer key={i} node={child} />)}
        </div>
      );

    case 'Row':
      return (
        <div
          className="flex flex-row items-center"
          style={{
            padding: typeof props?.padding === 'number' ? props.padding : 0,
            gap: typeof props?.spacing === 'number' ? props.spacing : 0,
            justifyContent: props?.arrangement === 'spaceBetween' ? 'space-between' : 
                           props?.arrangement === 'spaceEvenly' ? 'space-evenly' : 'flex-start',
          }}
        >
          {children?.map((child, i) => <LayoutRenderer key={i} node={child} />)}
        </div>
      );

    case 'Text':
      const text = String(props?.text || '');
      const fontSize = typeof props?.size === 'number' ? props.size : 14;
      const fontWeight = props?.weight === 'bold' ? 700 : 
                        props?.weight === 'semibold' ? 600 :
                        props?.weight === 'medium' ? 500 : 400;
      const color = typeof props?.color === 'string' ? props.color : '#000000';
      
      return (
        <span style={{ fontSize, fontWeight, color }}>
          {text.replace(/\{\{.*?\}\}/g, '●●●')}
        </span>
      );

    case 'Button':
      return (
        <button
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            props?.variant === 'outlined' 
              ? 'border border-blue-500 text-blue-500 bg-white' 
              : 'bg-blue-500 text-white'
          } ${props?.fillWidth ? 'w-full' : ''}`}
        >
          {String(props?.text || 'Button').replace(/\{\{.*?\}\}/g, '●●●')}
        </button>
      );

    case 'Image':
      return (
        <div
          className="bg-gray-200 rounded-lg flex items-center justify-center"
          style={{
            height: typeof props?.height === 'number' ? props.height : 100,
            borderRadius: typeof props?.cornerRadius === 'number' ? props.cornerRadius : 0,
          }}
        >
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      );

    case 'Badge':
      return (
        <span
          className="px-2 py-1 text-xs font-medium rounded-full"
          style={{
            backgroundColor: typeof props?.color === 'string' ? props.color + '20' : '#e0e0e0',
            color: typeof props?.color === 'string' ? props.color : '#333',
          }}
        >
          {String(props?.text || 'Badge').replace(/\{\{.*?\}\}/g, '●●●')}
        </span>
      );

    case 'Avatar':
      return (
        <div
          className="bg-gray-300 rounded-full flex items-center justify-center"
          style={{
            width: typeof props?.size === 'number' ? props.size : 40,
            height: typeof props?.size === 'number' ? props.size : 40,
          }}
        >
          <svg className="w-1/2 h-1/2 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
        </div>
      );

    case 'Card':
      return (
        <div
          className="bg-white rounded-lg shadow border border-gray-100"
          style={{ padding: typeof props?.padding === 'number' ? props.padding : 16 }}
        >
          {children?.map((child, i) => <LayoutRenderer key={i} node={child} />)}
        </div>
      );

    case 'Divider':
      return <div className="h-px bg-gray-200 my-2" />;

    case 'Spacer':
      return <div style={{ height: typeof props?.height === 'number' ? props.height : 16 }} />;

    case 'Checkbox':
      return (
        <div className="w-5 h-5 rounded border-2 border-gray-300 flex items-center justify-center">
          <div className="w-3 h-3 bg-blue-500 rounded-sm opacity-50" />
        </div>
      );

    case 'Switch':
      return (
        <div className="w-10 h-6 bg-blue-500 rounded-full flex items-center px-1">
          <div className="w-4 h-4 bg-white rounded-full ml-auto" />
        </div>
      );

    case 'Stepper':
      return (
        <div className="flex items-center gap-2">
          <button className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-lg">-</button>
          <span className="w-8 text-center">1</span>
          <button className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-lg">+</button>
        </div>
      );

    case 'Chip':
      return (
        <span className="px-3 py-1 text-sm rounded-full bg-gray-100 border border-gray-200">
          {String(props?.text || 'Chip')}
        </span>
      );

    case 'IconButton':
      return (
        <button className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </button>
      );

    case 'FAB':
      return (
        <button className="w-14 h-14 rounded-full bg-blue-500 text-white shadow-lg flex items-center justify-center fixed bottom-20 right-4">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      );

    case 'ListTile':
      const title = props?.title as string | undefined;
      const subtitle = props?.subtitle as string | undefined;
      const titleColor = props?.titleColor as string | undefined;
      const trailing = props?.trailing as import('./compiler').JsonNode | undefined;
      
      return (
        <div className="flex items-center py-3 gap-4">
          <div className="flex-1">
            <div className="font-medium text-sm" style={{ color: titleColor || '#000' }}>
              {title || ''}
            </div>
            {subtitle && (
              <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>
            )}
          </div>
          {trailing && <LayoutRenderer node={trailing} />}
          {!trailing && (
            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </div>
      );

    case 'If':
      // For preview, just show the "then" branch
      const slots = node.slots as Record<string, import('./compiler').JsonNode[]>;
      return (
        <>
          {slots?.then?.map((child, i) => <LayoutRenderer key={i} node={child} />)}
        </>
      );

    case 'ForEach':
      // For preview, show a few placeholder items
      return (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="opacity-75">
              {children?.map((child, j) => <LayoutRenderer key={j} node={child} />)}
            </div>
          ))}
        </div>
      );

    default:
      // Generic container
      return (
        <div className="p-2 border border-dashed border-gray-300 rounded text-xs text-gray-400">
          {type}
          {children?.map((child, i) => <LayoutRenderer key={i} node={child} />)}
        </div>
      );
  }
}

export default App;
